import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Space,
  Statistic,
  Table,
  Divider,
  Alert,
  Modal,
  Tag,
  Descriptions,
  Spin,
} from 'antd';
import { ReloadOutlined, FileDoneOutlined, FileTextOutlined, PrinterOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';
import { LoginContext } from '../../../context/loginContext';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const formatFechaConHora = (timestamp) => {
  if (!timestamp) return '-';
  if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
    return dayjs(timestamp.replace(' ', 'T')).format('DD/MM/YYYY HH:mm');
  }
  return dayjs(timestamp).format('DD/MM/YYYY HH:mm');
};

const formatFechaRendicion = (record) => {
  const fechaBase = record?.rec_fecha_hora_cierre || record?.rec_fecha_turno;
  if (!fechaBase) return '-';
  if (typeof fechaBase === 'string' && !fechaBase.includes('Z') && !fechaBase.includes('+') && !fechaBase.match(/-\d{2}:\d{2}$/)) {
    return dayjs(fechaBase.replace(' ', 'T')).format('DD/MM/YYYY');
  }
  return dayjs(fechaBase).format('DD/MM/YYYY');
};

const tagColorFormaPago = (nombre) => {
  const m = (nombre || '').toLowerCase();
  if (m === 'efectivo') return 'green';
  if (m === 'transferencia') return 'blue';
  if (m === 'qr') return 'cyan';
  if (m === 'tarjeta') return 'purple';
  return 'orange';
};

/** Igual que Caja diaria: compras con varias formas desde formas_pago_compra */
const renderFormaPagoMovimiento = (_, record) => {
  const formas = record.formas_pago_compra;
  if (formas && Array.isArray(formas) && formas.length > 0) {
    return (
      <Space wrap size="small">
        {formas.map((fp, idx) => (
          <Tag key={idx} color={tagColorFormaPago(fp.forma_pago)}>
            {fp.forma_pago} (${parseFloat(fp.monto).toFixed(2)})
          </Tag>
        ))}
      </Space>
    );
  }
  const metodo = record.caj_metodo_pago;
  if (!metodo) return '-';
  return <Tag color={tagColorFormaPago(metodo)}>{metodo}</Tag>;
};

const renderMontoMovimiento = (_, record) => {
  const v = record.total_comprobante != null ? record.total_comprobante : record.caj_monto;
  const ingreso = record.caj_tipo_movimiento === 'ingreso';
  return (
    <span style={{ color: ingreso ? '#52c41a' : '#ff4d4f' }}>
      {ingreso ? '+' : '-'}${parseFloat(v || 0).toFixed(2)}
    </span>
  );
};

const getConceptoMovimiento = (movimiento) => {
  if (movimiento?.caj_tipo_referencia === 'gasto') {
    const obs = (movimiento?.gasto_observaciones || '').trim();
    const base = movimiento?.caj_concepto || movimiento?.cuenta_nombre || 'Gasto';
    return obs ? `${base} - ${obs}` : base;
  }
  return (
    movimiento?.venta_detalle_rendicion ||
    movimiento?.cuenta_nombre ||
    movimiento?.caj_concepto ||
    '-'
  );
};

const renderConceptoMovimiento = (_, movimiento) => {
  const concepto = getConceptoMovimiento(movimiento);
  const conceptoBase = movimiento?.caj_concepto;
  const mostrarSubtitulo =
    movimiento?.venta_detalle_rendicion &&
    conceptoBase &&
    conceptoBase !== movimiento.venta_detalle_rendicion;

  return (
    <div>
      <div style={{ color: '#ffffff' }}>{concepto}</div>
      {mostrarSubtitulo && (
        <div style={{ color: '#8c8c8c', fontSize: 11, marginTop: 2 }}>
          {conceptoBase}
        </div>
      )}
    </div>
  );
};

const RendicionCaja = () => {
  const { hasPermission, permissions } = usePermissions();
  const { user } = useContext(LoginContext);
  const puedeVerTodos =
    (Array.isArray(permissions) && permissions.includes('*')) ||
    hasPermission('rendicion_caja', 'listar_todas');
  const miUsuarioId = Number(user?.id ?? user?.usu_id);
  const [usuarioRendicionId, setUsuarioRendicionId] = useState(null);
  const [listaUsuarios, setListaUsuarios] = useState([]);

  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [rendiciones, setRendiciones] = useState([]);
  const [rangoFechasHistorial, setRangoFechasHistorial] = useState([
    dayjs().subtract(29, 'day'),
    dayjs(),
  ]);
  const [filtroUsuarioHistorial, setFiltroUsuarioHistorial] = useState(null);
  const [paginationHistorial, setPaginationHistorial] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [formAjuste] = Form.useForm();
  const [modalAjusteVisible, setModalAjusteVisible] = useState(false);
  const [savingAjuste, setSavingAjuste] = useState(false);
  const [arqueoVisible, setArqueoVisible] = useState(false);
  const [arqueoData, setArqueoData] = useState(null);
  const [arqueoRendicion, setArqueoRendicion] = useState(null);
  const [loadingArqueo, setLoadingArqueo] = useState(false);
  const [controlesMovimientosVisible, setControlesMovimientosVisible] = useState(false);
  const [movimientoDetalle, setMovimientoDetalle] = useState(null);
  const [ventaDetallesArticulos, setVentaDetallesArticulos] = useState(null);
  const [loadingVentaDetalles, setLoadingVentaDetalles] = useState(false);
  const [modalAvisoPrevioVisible, setModalAvisoPrevioVisible] = useState(true);
  const [savingRendicion, setSavingRendicion] = useState(false);
  const submittingRendicionRef = useRef(false);
  const [avisoPendientesCobro, setAvisoPendientesCobro] = useState({
    canchas: [],
    mesas: [],
    loading: true,
  });

  useEffect(() => {
    if (!puedeVerTodos) return;
    if (Number.isFinite(miUsuarioId)) {
      setUsuarioRendicionId((prev) => (prev == null ? miUsuarioId : prev));
    }
  }, [puedeVerTodos, miUsuarioId]);

  useEffect(() => {
    if (!puedeVerTodos || !hasPermission('usuarios', 'ver')) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get('/gestion/usuarios?incluir_inactivos=false');
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const staff = (Array.isArray(data) ? data : []).filter((u) => u.usu_tipousuario === 1);
        if (!cancelled) setListaUsuarios(staff);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [puedeVerTodos, hasPermission]);

  useEffect(() => {
    if (!hasPermission('cobros', 'ver')) {
      setAvisoPendientesCobro({ canchas: [], mesas: [], loading: false });
      return;
    }
    let cancelled = false;
    (async () => {
      setAvisoPendientesCobro((p) => ({ ...p, loading: true }));
      try {
        const fecha = dayjs().format('YYYY-MM-DD');
        const r = await apiClient.get(`/gestion/cobros/destinos-pendientes?fecha=${fecha}`);
        if (!r.ok || cancelled) {
          if (!cancelled) setAvisoPendientesCobro({ canchas: [], mesas: [], loading: false });
          return;
        }
        const data = await r.json();
        const conDeuda = (Array.isArray(data) ? data : []).filter(
          (d) => parseFloat(d.total_pendiente || 0) > 0.009
        );
        const canchasSet = new Set();
        const mesasSet = new Set();
        const nombreCanchaDesdeTurno = (d) => {
          const cn = (d.cancha_nombre || '').trim();
          if (cn) return cn;
          const n = (d.nombre || '').trim();
          const m = n.match(/^(.+?)\s+\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}/);
          return m ? m[1].trim() : '';
        };
        for (const d of conDeuda) {
          if (d.tipo === 'cancha_turno') {
            const cn = nombreCanchaDesdeTurno(d);
            if (cn) canchasSet.add(cn);
          } else if (d.tipo === 'cancha' && d.nombre) {
            canchasSet.add(String(d.nombre).trim());
          } else if (d.tipo === 'mesa' && d.nombre) {
            mesasSet.add(String(d.nombre).trim());
          }
        }
        const canchas = [...canchasSet].sort((a, b) =>
          a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' })
        );
        const mesas = [...mesasSet].sort((a, b) =>
          a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' })
        );
        if (!cancelled) setAvisoPendientesCobro({ canchas, mesas, loading: false });
      } catch {
        if (!cancelled) setAvisoPendientesCobro({ canchas: [], mesas: [], loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasPermission]);

  const nombreUsuarioSeleccionado = useMemo(() => {
    if (!Number.isFinite(usuarioRendicionId)) return '';
    const u = listaUsuarios.find((x) => x.usu_id === usuarioRendicionId);
    return u?.usu_usuario || `#${usuarioRendicionId}`;
  }, [listaUsuarios, usuarioRendicionId]);

  const loadResumen = useCallback(async () => {
    setLoadingResumen(true);
    setResumen(null);
    try {
      const params = new URLSearchParams();
      if (puedeVerTodos && usuarioRendicionId != null) {
        params.set('usuario_id', String(usuarioRendicionId));
      }
      const qs = params.toString();
      const url = `/gestion/rendicion-caja/recursos/resumen-pendiente${qs ? `?${qs}` : ''}`;
      const r = await apiClient.get(url);
      if (r.ok) {
        const data = await r.json();
        setResumen(data);
        form.setFieldsValue({ monto_efectivo_declarado: data.saldo_efectivo_esperado });
      } else {
        message.error('Error al cargar resumen');
      }
    } catch (e) {
      message.error('Error al cargar resumen');
    } finally {
      setLoadingResumen(false);
    }
  }, [puedeVerTodos, usuarioRendicionId, form]);

  const loadRendiciones = useCallback(async (
    page = 1,
    pageSize = 10,
    opts = {}
  ) => {
    const fechas = opts.fechas ?? rangoFechasHistorial;
    const usuarioId = opts.usuarioId !== undefined ? opts.usuarioId : filtroUsuarioHistorial;

    if (!fechas?.[0] || !fechas?.[1]) {
      message.warning('Seleccioná el rango de fechas');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('fecha_desde', fechas[0].format('YYYY-MM-DD'));
      params.set('fecha_hasta', fechas[1].format('YYYY-MM-DD'));
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (puedeVerTodos && usuarioId != null) {
        params.set('usuario_id', String(usuarioId));
      }
      const r = await apiClient.get(`/gestion/rendicion-caja?${params.toString()}`);
      if (r.ok) {
        const result = await r.json();
        const data = Array.isArray(result)
          ? result
          : (Array.isArray(result?.data) ? result.data : []);
        const total = Array.isArray(result) ? result.length : (result?.total ?? 0);
        setRendiciones(data);
        setPaginationHistorial({
          current: Array.isArray(result) ? page : (result.page || page),
          pageSize: Array.isArray(result) ? pageSize : (result.pageSize || pageSize),
          total,
        });
      } else {
        const err = await r.json().catch(() => ({}));
        message.error(err.message || 'Error al cargar rendiciones');
      }
    } catch (e) {
      message.error('Error al cargar rendiciones');
    } finally {
      setLoading(false);
    }
  }, [rangoFechasHistorial, filtroUsuarioHistorial, puedeVerTodos]);

  const handleBuscarHistorial = () => {
    loadRendiciones(1, paginationHistorial.pageSize);
  };

  const handleTableHistorialChange = (pag) => {
    loadRendiciones(pag.current, pag.pageSize);
  };

  const historialInicialCargado = useRef(false);
  useEffect(() => {
    if (historialInicialCargado.current) return;
    historialInicialCargado.current = true;
    const fechasDefault = [dayjs().subtract(29, 'day'), dayjs()];
    loadRendiciones(1, 10, { fechas: fechasDefault, usuarioId: null });
  }, [loadRendiciones]);

  useEffect(() => {
    if (puedeVerTodos && usuarioRendicionId == null) return;
    loadResumen();
  }, [puedeVerTodos, usuarioRendicionId, loadResumen]);

  useEffect(() => {
    if (!movimientoDetalle) {
      setVentaDetallesArticulos(null);
      setLoadingVentaDetalles(false);
      return;
    }
    const tipo = (movimientoDetalle.caj_tipo_referencia || '').toLowerCase();
    const venId = movimientoDetalle.caj_referencia_id;
    if (tipo !== 'venta' || venId == null) {
      setVentaDetallesArticulos(null);
      setLoadingVentaDetalles(false);
      return;
    }
    let cancelled = false;
    setLoadingVentaDetalles(true);
    setVentaDetallesArticulos(null);
    (async () => {
      try {
        const r = await apiClient.get(`/gestion/rendicion-caja/recursos/venta/${venId}/detalles`);
        if (cancelled) return;
        if (!r.ok) {
          setVentaDetallesArticulos([]);
          return;
        }
        const data = await r.json();
        setVentaDetallesArticulos(Array.isArray(data.detalles) ? data.detalles : []);
      } catch {
        if (!cancelled) setVentaDetallesArticulos([]);
      } finally {
        if (!cancelled) setLoadingVentaDetalles(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [movimientoDetalle]);

  const handleOpenModalAjuste = () => {
    formAjuste.resetFields();
    formAjuste.setFieldsValue({
      fecha: dayjs(),
      tipo_movimiento: 'ingreso',
      metodo_pago: 'efectivo',
    });
    setModalAjusteVisible(true);
  };

  const handleGuardarAjuste = async () => {
    if (savingAjuste) return;
    try {
      setSavingAjuste(true);
      const values = await formAjuste.validateFields();
      const dataToSend = {
        fecha: values.fecha.format('YYYY-MM-DD'),
        tipo_movimiento: values.tipo_movimiento,
        concepto: values.concepto,
        monto: values.monto,
        metodo_pago: values.metodo_pago,
        tipo_referencia: 'ajuste',
      };
      const r = await apiClient.post('/gestion/caja-diaria', dataToSend);
      if (r.ok) {
        message.success('Movimiento registrado en tu caja');
        setModalAjusteVisible(false);
        formAjuste.resetFields();
        loadResumen();
        loadRendiciones(paginationHistorial.current, paginationHistorial.pageSize);
      } else {
        const err = await r.json();
        message.error(err.message || 'Error al registrar el movimiento');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('Error al guardar el movimiento');
    } finally {
      setSavingAjuste(false);
    }
  };

  const handleVerArqueo = async (record) => {
    setLoadingArqueo(true);
    setArqueoRendicion(record);
    setArqueoData(null);
    setArqueoVisible(true);
    try {
      const r = await apiClient.get(`/gestion/rendicion-caja/${record.rec_id}/arqueo`);
      if (r.ok) {
        const data = await r.json();
        setArqueoData(data);
      } else {
        message.error('Error al cargar arqueo');
        setArqueoVisible(false);
      }
    } catch (e) {
      message.error('Error al cargar arqueo');
      setArqueoVisible(false);
    } finally {
      setLoadingArqueo(false);
    }
  };

  const handleImprimirArqueo = () => {
    if (!arqueoData || !arqueoRendicion) return;
    const totalIng = parseFloat(arqueoRendicion.rec_total_ingresos != null ? arqueoRendicion.rec_total_ingresos : 0);
    const totalEgr = parseFloat(arqueoRendicion.rec_total_egresos != null ? arqueoRendicion.rec_total_egresos : 0);
    const sist = parseFloat(arqueoRendicion.rec_saldo_efectivo_esperado != null ? arqueoRendicion.rec_saldo_efectivo_esperado : 0);
    const decl = parseFloat(arqueoRendicion.rec_monto_efectivo_declarado != null ? arqueoRendicion.rec_monto_efectivo_declarado : 0);
    const diff = decl - sist;
    const diffTexto = diff === 0 ? '$0.00' : diff > 0 ? `+$${diff.toFixed(2)} (sobrante)` : `$${diff.toFixed(2)} (faltante)`;
    const movsSinRetiro = (arqueoData.movimientos || []).filter((m) => !(m.caj_concepto || '').includes('Cierre de caja'));
    const formasPagoIng = (arqueoData.formas_pago || []).filter((fp) => fp.total_ingresos > 0).map((fp) => `${fp.forma_pago}: $${parseFloat(fp.total_ingresos).toFixed(2)}`).join(', ');
    const formasPagoEgr = totalEgr > 0 ? (arqueoData.formas_pago || []).filter((fp) => fp.total_egresos > 0).map((fp) => `${fp.forma_pago}: $${parseFloat(fp.total_egresos).toFixed(2)}`).join(', ') : '';
    const movRows = movsSinRetiro.map((m) => {
      const fechaHora = m.caj_fecha_creacion ? formatFechaConHora(m.caj_fecha_creacion) : (m.caj_fecha || '-');
      const cuenta = getConceptoMovimiento(m);
      const tipo = (m.caj_tipo_movimiento || '').toUpperCase();
      const monto = parseFloat(m.caj_monto || 0);
      const signo = m.caj_tipo_movimiento === 'ingreso' ? '+' : '-';
      return `<tr><td>${fechaHora}</td><td>${tipo}</td><td>${cuenta}</td><td>${m.caj_metodo_pago || '-'}</td><td style="text-align:right">${signo}$${Math.abs(monto).toFixed(2)}</td></tr>`;
    }).join('');
    const titulo = arqueoRendicion.usuario_nombre
      ? `Rendición - ${arqueoRendicion.usuario_nombre} - ${dayjs(arqueoRendicion.rec_fecha_turno).format('DD/MM/YYYY')}`
      : `Rendición - ${dayjs(arqueoRendicion.rec_fecha_turno).format('DD/MM/YYYY')}`;
    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Arqueo de caja - ${titulo}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #333; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    .subt { font-size: 11px; color: #666; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
    th { background: #f5f5f5; font-weight: 600; }
    .resumen { margin: 16px 0; }
    .resumen p { margin: 4px 0; }
    .totales { font-weight: 600; margin-top: 12px; }
  </style>
</head>
<body>
  <h1>Arqueo de caja - Rendición</h1>
  <p class="subt">${titulo} | Cierre: ${formatFechaConHora(arqueoRendicion.rec_fecha_hora_cierre)}</p>
  <div class="resumen">
    <p><strong>Total ingresos:</strong> $${totalIng.toFixed(2)} ${formasPagoIng ? `(${formasPagoIng})` : ''}</p>
    <p><strong>Total egresos:</strong> $${totalEgr.toFixed(2)} ${formasPagoEgr ? `(${formasPagoEgr})` : ''}</p>
    <p><strong>Efectivo (sistema):</strong> $${sist.toFixed(2)}</p>
    <p><strong>Efectivo declarado:</strong> $${decl.toFixed(2)}</p>
    <p class="totales"><strong>Diferencia (faltante/sobrante):</strong> ${diffTexto}</p>
  </div>
  <h2 style="font-size: 14px; margin-top: 20px;">Detalle de movimientos</h2>
  <table>
    <thead><tr><th>Fecha / Hora</th><th>Tipo</th><th>Concepto / Cuenta</th><th>Forma de pago</th><th>Monto</th></tr></thead>
    <tbody>${movRows || '<tr><td colspan="5">Sin movimientos</td></tr>'}</tbody>
  </table>
  <p style="margin-top: 16px; font-size: 11px; color: #888;">Documento generado para presentación de rendición de caja.</p>
</body>
</html>`;
    const ventana = window.open('', '_blank');
    ventana.document.write(html);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => { ventana.print(); }, 300);
  };

  const handleSubmit = async () => {
    if (submittingRendicionRef.current) return;
    submittingRendicionRef.current = true;
    setSavingRendicion(true);
    try {
      const values = await form.validateFields();
      const payload = {
        monto_efectivo_declarado: values.monto_efectivo_declarado,
        observaciones: values.observaciones || null,
      };
      if (puedeVerTodos && usuarioRendicionId != null) {
        payload.usuario_id = usuarioRendicionId;
      }
      const r = await apiClient.post('/gestion/rendicion-caja', payload);
      if (r.ok) {
        message.success('Rendición registrada correctamente');
        setResumen(null);
        loadResumen();
        loadRendiciones(paginationHistorial.current, paginationHistorial.pageSize);
        form.setFieldsValue({ monto_efectivo_declarado: undefined, observaciones: undefined });
      } else {
        const err = await r.json();
        message.error(err.message || 'Error al registrar rendición');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('Error al enviar');
    } finally {
      submittingRendicionRef.current = false;
      setSavingRendicion(false);
    }
  };

  const sinPendientes = resumen && resumen.cantidad_movimientos === 0;

  const linkCobros = hasPermission('cobros', 'ver') ? (
    <Link to="/gestion/cobros">Cobros</Link>
  ) : (
    <strong>Cobros</strong>
  );
  const linkCtacte = hasPermission('cuenta_corriente', 'ver') ? (
    <Link to="/gestion/cuenta-corriente">Cuenta corriente</Link>
  ) : (
    <strong>Cuenta corriente</strong>
  );

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <Modal
          title="Revisá cobros y cuenta corriente antes de rendir"
          open={modalAvisoPrevioVisible}
          closable={false}
          maskClosable={false}
          keyboard={false}
          footer={(
            <Button
              type="primary"
              disabled={hasPermission('cobros', 'ver') && avisoPendientesCobro.loading}
              onClick={() => setModalAvisoPrevioVisible(false)}
            >
              Aceptar
            </Button>
          )}
          width={620}
        >
          <p style={{ marginBottom: 12, lineHeight: 1.65 }}>
            Antes de registrar la rendición <strong>están pendientes de cobro</strong> lo siguiente:
          </p>
          {hasPermission('cobros', 'ver') && avisoPendientesCobro.loading && (
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
              <Spin />
            </div>
          )}
          {hasPermission('cobros', 'ver') && !avisoPendientesCobro.loading && (
            <ul style={{ margin: '0 0 16px 0', paddingLeft: 20, lineHeight: 1.6 }}>
              {avisoPendientesCobro.canchas.map((c) => (
                <li key={`c-${c}`} style={{ marginBottom: 6, color: '#e0e0e0' }}>
                  <strong>{c}</strong>: hay deudas pendientes.
                </li>
              ))}
              {avisoPendientesCobro.mesas.map((m) => (
                <li key={`m-${m}`} style={{ marginBottom: 6, color: '#e0e0e0' }}>
                  Mesa <strong>{m}</strong>: hay deudas pendientes.
                </li>
              ))}
              {avisoPendientesCobro.canchas.length === 0 && avisoPendientesCobro.mesas.length === 0 && (
                <li style={{ color: '#888' }}>
                  No hay turnos en canchas ni mesas con saldo pendiente para hoy.
                </li>
              )}
            </ul>
          )}
          {!hasPermission('cobros', 'ver') && (
            <p style={{ marginBottom: 16, color: '#a0a0a0', fontSize: 13 }}>
              No tenés permiso para ver el detalle de cobros; igual podés revisar con quien corresponda.
            </p>
          )}
          <p style={{ marginBottom: 0, lineHeight: 1.65 }}>
            Conviene pasar por {linkCobros} para cobrar lo que falte y por {linkCtacte} por si hay{' '}
            <strong>pagos de jugadores en cuenta corriente</strong> sin registrar. Así la caja que rendís refleja mejor la situación real.
          </p>
        </Modal>

        <h1 style={{ marginBottom: 8, color: '#fff' }}>Rendición de caja</h1>
        <p style={{ color: '#b0b0b0', marginBottom: 24 }}>
          Al finalizar la jornada se rinden los movimientos pendientes del usuario (compras, gastos, cobros).
          {!puedeVerTodos && (
            <>
              {' '}La rendición incluye solo lo pendiente de <strong>tu usuario</strong>.
            </>
          )}
          {puedeVerTodos && (
            <>
              {' '}Como administrador podés consultar y registrar la rendición de <strong>cualquier usuario</strong> del listado.
            </>
          )}
          {' '}Con <strong>Nuevo ajuste</strong> ingresás o retirás dinero de la <strong>caja del usuario logueado</strong> (vos), igual que en Caja diaria.
        </p>

        <Card
          title="Rendir caja"
          extra={(
            <Space>
              {(hasPermission('caja_diaria', 'crear') ||
                hasPermission('rendicion_caja', 'crear_ajuste')) && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModalAjuste} size="small">
                  Nuevo ajuste
                </Button>
              )}
              <Button icon={<ReloadOutlined />} onClick={loadResumen} loading={loadingResumen} size="small">
                Actualizar
              </Button>
            </Space>
          )}
          style={{ marginBottom: 24 }}
        >
          {puedeVerTodos && listaUsuarios.length > 0 && (
            <Form.Item label="Usuario a rendir" style={{ maxWidth: 360, marginBottom: 16 }}>
              <Select
                showSearch
                optionFilterProp="children"
                value={usuarioRendicionId}
                onChange={(v) => setUsuarioRendicionId(v)}
                options={listaUsuarios.map((u) => ({
                  value: u.usu_id,
                  label: u.usu_usuario,
                }))}
                placeholder="Elegí usuario"
              />
            </Form.Item>
          )}
          {puedeVerTodos && usuarioRendicionId != null && (
            <div style={{ color: '#a0a0a0', marginBottom: 16, fontSize: 13 }}>
              Rendición para: <strong style={{ color: '#e0e0e0' }}>{nombreUsuarioSeleccionado}</strong>
              {usuarioRendicionId === miUsuarioId ? ' (vos)' : ''}
            </div>
          )}
          {sinPendientes && (
            <Alert
              type="info"
              message={
                puedeVerTodos && usuarioRendicionId != null && usuarioRendicionId !== miUsuarioId
                  ? `${nombreUsuarioSeleccionado} no tiene movimientos pendientes de rendición.`
                  : 'No tenés movimientos pendientes de rendición.'
              }
              description="Realizá compras, gastos o cobros y volvé a actualizar el resumen."
              style={{ marginTop: 16 }}
            />
          )}

          {resumen && !sinPendientes && (
            <>
              <Divider />
              <div style={{ marginBottom: 16 }}>
                <strong style={{ color: '#b0b0b0' }}>Movimientos pendientes de rendición:</strong>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 24, alignItems: 'flex-start' }}>
                <div>
                  <Statistic title="Total ingresos" value={resumen.total_ingresos} prefix="$" valueStyle={{ color: '#52c41a' }} />
                  {resumen.formas_pago && resumen.formas_pago.length > 0 && (
                    <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid #52c41a' }}>
                      {resumen.formas_pago
                        .filter((fp) => fp.total_ingresos > 0)
                        .map((fp) => (
                          <div key={fp.forma_pago} style={{ color: '#b0b0b0', fontSize: 13 }}>
                            {fp.forma_pago}: <span style={{ color: '#52c41a' }}>${fp.total_ingresos.toFixed(2)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <div>
                  <Statistic title="Total egresos" value={resumen.total_egresos} prefix="$" valueStyle={{ color: '#ff4d4f' }} />
                  {resumen.formas_pago && resumen.formas_pago.length > 0 && (
                    <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid #ff4d4f' }}>
                      {resumen.formas_pago
                        .filter((fp) => fp.total_egresos > 0)
                        .map((fp) => (
                          <div key={fp.forma_pago} style={{ color: '#b0b0b0', fontSize: 13 }}>
                            {fp.forma_pago}: <span style={{ color: '#ff4d4f' }}>${fp.total_egresos.toFixed(2)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <div>
                  <Statistic
                    title={<span>Efectivo esperado <span style={{ fontWeight: 'normal', fontSize: 12, color: '#888' }}>(ingresos − egresos en efectivo)</span></span>}
                    value={resumen.saldo_efectivo_esperado}
                    prefix="$"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </div>
                <div>
                  <Statistic title="Movimientos" value={resumen.cantidad_movimientos} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => setControlesMovimientosVisible(true)}
                >
                  Controles de movimientos
                </Button>
              </div>
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                  name="monto_efectivo_declarado"
                  label="Efectivo que se entrega ($)"
                  rules={[{ required: true }]}
                >
                  <Input type="number" step={0.01} min={0} placeholder="Monto contado en caja" />
                </Form.Item>
                <Form.Item name="observaciones" label="Observaciones">
                  <Input.TextArea rows={2} placeholder="Opcional" />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<FileDoneOutlined />}
                  loading={savingRendicion}
                  disabled={savingRendicion}
                >
                  Registrar rendición
                </Button>
              </Form>
            </>
          )}
        </Card>

        <Modal
          title="Control de movimientos pendientes"
          open={controlesMovimientosVisible}
          onCancel={() => setControlesMovimientosVisible(false)}
          width={950}
          footer={[
            <Button key="cerrar" onClick={() => setControlesMovimientosVisible(false)}>Cerrar</Button>,
          ]}
        >
          {!resumen && <Alert type="info" message="No hay resumen cargado. Usá Actualizar en la tarjeta Rendir caja." />}
          {resumen && (
            <>
              <div style={{ marginBottom: 12 }}>
                <Space wrap size="large">
                  <Statistic title="Ingresos" value={resumen.total_ingresos} prefix="$" valueStyle={{ color: '#52c41a', fontSize: 16 }} />
                  <Statistic title="Egresos" value={resumen.total_egresos} prefix="$" valueStyle={{ color: '#ff4d4f', fontSize: 16 }} />
                  <Statistic title="Efectivo esperado" value={resumen.saldo_efectivo_esperado} prefix="$" valueStyle={{ color: '#1890ff', fontSize: 16 }} />
                  <Statistic title="Movimientos" value={resumen.cantidad_movimientos} valueStyle={{ fontSize: 16 }} />
                </Space>
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <Table
                size="small"
                dataSource={resumen.movimientos || []}
                rowKey={(r, i) => r.caj_id || i}
                pagination={{ pageSize: 12 }}
                columns={[
                  { title: 'Fecha / Hora', key: 'fecha', width: 150, render: (_, m) => formatFechaConHora(m.caj_fecha_creacion || m.caj_fecha) },
                  { title: 'Tipo', dataIndex: 'caj_tipo_movimiento', key: 'tipo', width: 90, render: (t) => (t || '').toUpperCase() },
                  { title: 'Cuenta / Concepto', key: 'cuenta', render: (_, m) => m.cuenta_nombre || m.caj_concepto || '-' },
                  { title: 'Comprobante', dataIndex: 'numero_comprobante', key: 'comp', width: 140, render: (v) => v || '-' },
                  { title: 'Forma de pago', dataIndex: 'formas_pago_compra', key: 'fp', width: 220, render: renderFormaPagoMovimiento },
                  {
                    title: 'Monto',
                    dataIndex: 'caj_monto',
                    key: 'monto',
                    width: 120,
                    align: 'right',
                    render: renderMontoMovimiento,
                  },
                  {
                    title: '',
                    key: 'ver',
                    width: 52,
                    align: 'center',
                    render: (_, m) => (
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        aria-label="Ver detalle del movimiento"
                        onClick={() => setMovimientoDetalle(m)}
                      />
                    ),
                  },
                ]}
              />
            </>
          )}
        </Modal>

        <Modal
          title="Detalle del movimiento"
          open={movimientoDetalle != null}
          onCancel={() => setMovimientoDetalle(null)}
          footer={[
            <Button key="cerrar" onClick={() => setMovimientoDetalle(null)}>
              Cerrar
            </Button>,
          ]}
          width={720}
          destroyOnClose
        >
          {movimientoDetalle && (
            <>
              <Descriptions
                bordered
                column={1}
                size="small"
                styles={{
                  label: { width: 168, color: '#a0a0a0' },
                  content: { color: '#e8e8e8' },
                }}
              >
                <Descriptions.Item label="Fecha y hora">
                  {formatFechaConHora(movimientoDetalle.caj_fecha_creacion || movimientoDetalle.caj_fecha)}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo">
                  {(movimientoDetalle.caj_tipo_movimiento || '').toUpperCase()}
                </Descriptions.Item>
                <Descriptions.Item label="Monto">{renderMontoMovimiento(null, movimientoDetalle)}</Descriptions.Item>
                <Descriptions.Item label="Cuenta / categoría">
                  {movimientoDetalle.cuenta_nombre || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Concepto">
                  {movimientoDetalle.caj_concepto || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Comprobante">
                  {movimientoDetalle.numero_comprobante || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Forma de pago">
                  {renderFormaPagoMovimiento(null, movimientoDetalle)}
                </Descriptions.Item>
                {movimientoDetalle.total_comprobante != null && (
                  <Descriptions.Item label="Total comprobante">
                    ${parseFloat(movimientoDetalle.total_comprobante || 0).toFixed(2)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Referencia">
                  {movimientoDetalle.caj_tipo_referencia || '—'}
                  {movimientoDetalle.caj_referencia_id != null
                    ? ` · #${movimientoDetalle.caj_referencia_id}`
                    : ''}
                </Descriptions.Item>
                <Descriptions.Item label="ID movimiento caja">
                  {movimientoDetalle.caj_id ?? '—'}
                </Descriptions.Item>
              </Descriptions>

              {(movimientoDetalle.caj_tipo_referencia || '').toLowerCase() === 'venta'
                && movimientoDetalle.caj_referencia_id != null && (
                <>
                  <Divider style={{ margin: '20px 0 12px' }} />
                  <h4
                    style={{
                      marginBottom: 12,
                      color: '#fff',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: '1px solid #303030',
                      paddingBottom: 8,
                    }}
                  >
                    Artículos
                  </h4>
                  <Table
                    size="small"
                    loading={loadingVentaDetalles}
                    dataSource={ventaDetallesArticulos || []}
                    rowKey={(r) => r.ved_id}
                    pagination={false}
                    locale={{ emptyText: 'Sin líneas de artículos' }}
                    columns={[
                      {
                        title: 'Artículo',
                        dataIndex: 'articulo_nombre',
                        key: 'nombre',
                        ellipsis: true,
                      },
                      {
                        title: 'Cantidad',
                        dataIndex: 'ved_cantidad',
                        key: 'cant',
                        width: 88,
                        align: 'center',
                      },
                      {
                        title: 'Precio unit.',
                        key: 'pu',
                        width: 120,
                        align: 'right',
                        render: (_, r) => `$${parseFloat(r.ved_precio_unitario || 0).toFixed(2)}`,
                      },
                      {
                        title: 'Subtotal',
                        key: 'sub',
                        width: 120,
                        align: 'right',
                        render: (_, r) => `$${parseFloat(r.ved_subtotal || 0).toFixed(2)}`,
                      },
                    ]}
                  />
                </>
              )}
            </>
          )}
        </Modal>

        <Card
          title="Historial de rendiciones"
          extra={(
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadRendiciones(paginationHistorial.current, paginationHistorial.pageSize)}
              loading={loading}
              size="small"
            >
              Actualizar
            </Button>
          )}
        >
          <Space wrap align="end" style={{ marginBottom: 16 }}>
            <div>
              <div style={{ color: '#b0b0b0', marginBottom: 4, fontSize: 13 }}>Desde — Hasta</div>
              <RangePicker
                value={rangoFechasHistorial}
                onChange={(dates) => setRangoFechasHistorial(dates)}
                format="DD/MM/YYYY"
                allowClear={false}
              />
            </div>
            {puedeVerTodos && listaUsuarios.length > 0 && (
              <div>
                <div style={{ color: '#b0b0b0', marginBottom: 4, fontSize: 13 }}>Usuario</div>
                <Select
                  allowClear
                  placeholder="Todos los usuarios"
                  style={{ minWidth: 200 }}
                  value={filtroUsuarioHistorial}
                  onChange={(v) => setFiltroUsuarioHistorial(v ?? null)}
                  options={listaUsuarios.map((u) => ({
                    value: u.usu_id,
                    label: u.usu_usuario,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </div>
            )}
            <Button type="primary" onClick={handleBuscarHistorial} loading={loading}>
              Buscar
            </Button>
          </Space>
          <Table
            dataSource={rendiciones}
            rowKey="rec_id"
            loading={loading}
            size="small"
            columns={[
              { title: 'Fecha', key: 'fecha', render: (_, record) => formatFechaRendicion(record) },
              { title: 'Usuario', dataIndex: 'usuario_nombre', key: 'usuario', render: (v) => v || '-' },
              { title: 'Turno', dataIndex: 'turno_nombre', key: 'turno', render: (v) => v || '-' },
              { title: 'Cierre', dataIndex: 'rec_fecha_hora_cierre', key: 'cierre', render: formatFechaConHora },
              { title: 'Ingresos', dataIndex: 'rec_total_ingresos', key: 'ing', render: (v) => `$${parseFloat(v || 0).toFixed(2)}` },
              { title: 'Egresos', dataIndex: 'rec_total_egresos', key: 'egr', render: (v) => `$${parseFloat(v || 0).toFixed(2)}` },
              {
                title: 'Efectivo (sistema)',
                dataIndex: 'rec_saldo_efectivo_esperado',
                key: 'efectivo_sistema',
                render: (v) => `$${parseFloat(v != null ? v : 0).toFixed(2)}`,
              },
              {
                title: 'Efectivo declarado',
                dataIndex: 'rec_monto_efectivo_declarado',
                key: 'efectivo_declarado',
                render: (v) => `$${parseFloat(v || 0).toFixed(2)}`,
              },
              {
                title: 'Faltante / Sobrante',
                key: 'diferencia',
                width: 220,
                render: (_, record) => {
                  const sistema = parseFloat(record.rec_saldo_efectivo_esperado != null ? record.rec_saldo_efectivo_esperado : 0);
                  const declarado = parseFloat(record.rec_monto_efectivo_declarado != null ? record.rec_monto_efectivo_declarado : 0);
                  const diff = declarado - sistema;
                  let diffText = '$0.00';
                  if (diff > 0) diffText = `+$${diff.toFixed(2)} (sobrante)`;
                  else if (diff < 0) diffText = `$${diff.toFixed(2)} (faltante)`;
                  return (
                    <Space>
                      <span style={{ color: diff === 0 ? '#b0b0b0' : diff > 0 ? '#52c41a' : '#ff4d4f' }}>{diffText}</span>
                      <Button
                        type="link"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => handleVerArqueo(record)}
                      >
                        Ver arqueo
                      </Button>
                    </Space>
                  );
                },
              },
            ]}
            pagination={{
              current: paginationHistorial.current,
              pageSize: paginationHistorial.pageSize,
              total: paginationHistorial.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `${total} rendición${total !== 1 ? 'es' : ''}`,
            }}
            onChange={handleTableHistorialChange}
          />
        </Card>

        <Modal
          title={arqueoRendicion ? `Arqueo - ${arqueoRendicion.usuario_nombre || 'Rendición'} - ${arqueoRendicion.rec_fecha_turno ? dayjs(arqueoRendicion.rec_fecha_turno).format('DD/MM/YYYY') : ''}` : 'Arqueo de caja'}
          open={arqueoVisible}
          onCancel={() => { setArqueoVisible(false); setArqueoData(null); setArqueoRendicion(null); }}
          width={800}
          footer={[
            <Button key="cerrar" onClick={() => { setArqueoVisible(false); setArqueoData(null); setArqueoRendicion(null); }}>Cerrar</Button>,
            arqueoData && (
              <Button key="imprimir" type="primary" icon={<PrinterOutlined />} onClick={handleImprimirArqueo}>
                Imprimir / Guardar PDF
              </Button>
            ),
          ]}
        >
          {loadingArqueo && <div style={{ padding: 24, textAlign: 'center' }}>Cargando movimientos...</div>}
          {!loadingArqueo && arqueoData && arqueoRendicion && (
            <>
              <div style={{ marginBottom: 16 }}>
                <Space wrap size="large">
                  <Statistic title="Ingresos" value={arqueoRendicion.rec_total_ingresos} prefix="$" valueStyle={{ color: '#52c41a', fontSize: 16 }} />
                  <Statistic title="Egresos" value={arqueoRendicion.rec_total_egresos} prefix="$" valueStyle={{ color: '#ff4d4f', fontSize: 16 }} />
                  <Statistic title="Efectivo (sistema)" value={arqueoRendicion.rec_saldo_efectivo_esperado} prefix="$" valueStyle={{ fontSize: 16 }} />
                  <Statistic title="Efectivo declarado" value={arqueoRendicion.rec_monto_efectivo_declarado} prefix="$" valueStyle={{ fontSize: 16 }} />
                  <div>
                    <div style={{ color: '#b0b0b0', fontSize: 12, marginBottom: 2 }}>Faltante / Sobrante</div>
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: (() => {
                          const sist = parseFloat(arqueoRendicion.rec_saldo_efectivo_esperado != null ? arqueoRendicion.rec_saldo_efectivo_esperado : 0);
                          const decl = parseFloat(arqueoRendicion.rec_monto_efectivo_declarado || 0);
                          const d = decl - sist;
                          return d === 0 ? '#b0b0b0' : d > 0 ? '#52c41a' : '#ff4d4f';
                        })(),
                      }}
                    >
                      {(() => {
                        const sist = parseFloat(arqueoRendicion.rec_saldo_efectivo_esperado != null ? arqueoRendicion.rec_saldo_efectivo_esperado : 0);
                        const decl = parseFloat(arqueoRendicion.rec_monto_efectivo_declarado || 0);
                        const d = decl - sist;
                        return `${d >= 0 ? '+' : ''}$${d.toFixed(2)}`;
                      })()}
                    </span>
                  </div>
                </Space>
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <h4 style={{ marginBottom: 8, color: '#fff' }}>Movimientos incluidos en la rendición</h4>
              <Table
                size="small"
                dataSource={(arqueoData.movimientos || []).filter((m) => !(m.caj_concepto || '').includes('Cierre de caja'))}
                rowKey={(r, i) => r.caj_id || i}
                pagination={{ pageSize: 8 }}
                columns={[
                  { title: 'Fecha / Hora', key: 'fecha', width: 140, render: (_, m) => formatFechaConHora(m.caj_fecha_creacion || m.caj_fecha) },
                  { title: 'Tipo', dataIndex: 'caj_tipo_movimiento', key: 'tipo', width: 90, render: (t) => (t || '').toUpperCase() },
                  { title: 'Concepto / Cuenta', key: 'cuenta', render: renderConceptoMovimiento },
                  { title: 'Forma de pago', dataIndex: 'formas_pago_compra', key: 'fp', width: 220, render: renderFormaPagoMovimiento },
                  {
                    title: 'Monto',
                    dataIndex: 'caj_monto',
                    key: 'monto',
                    width: 100,
                    render: renderMontoMovimiento,
                  },
                ]}
              />
            </>
          )}
        </Modal>

        <Modal
          title="Nuevo ajuste manual"
          open={modalAjusteVisible}
          onOk={handleGuardarAjuste}
          onCancel={() => {
            setModalAjusteVisible(false);
            setSavingAjuste(false);
            formAjuste.resetFields();
          }}
          okText="Registrar"
          cancelText="Cancelar"
          confirmLoading={savingAjuste}
          width={500}
        >
          <Form form={formAjuste} layout="vertical">
            <Form.Item
              name="fecha"
              label="Fecha"
              rules={[{ required: true, message: 'Seleccioná la fecha' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item
              name="tipo_movimiento"
              label="Tipo de movimiento"
              rules={[{ required: true, message: 'Seleccioná el tipo' }]}
            >
              <Select>
                <Select.Option value="ingreso">Ingreso</Select.Option>
                <Select.Option value="egreso">Egreso</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="concepto"
              label="Concepto"
              rules={[{ required: true, message: 'Ingresá el concepto' }]}
            >
              <TextArea rows={2} placeholder="Descripción del movimiento" />
            </Form.Item>
            <Form.Item
              name="monto"
              label="Monto"
              rules={[{ required: true, message: 'Ingresá el monto' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0.01}
                step={0.01}
                formatter={(value) => `$ ${value}`}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="0.00"
              />
            </Form.Item>
            <Form.Item
              name="metodo_pago"
              label="Método de pago"
              rules={[{ required: true, message: 'Seleccioná el método' }]}
            >
              <Select>
                <Select.Option value="efectivo">Efectivo</Select.Option>
                <Select.Option value="transferencia">Transferencia</Select.Option>
                <Select.Option value="tarjeta">Tarjeta</Select.Option>
                <Select.Option value="mixto">Mixto</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default RendicionCaja;
